import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Loading from "../../Components/loading";
import FileUploadConformationModal from "../../Components/FileUploadConformationModal";
import bgImage from "../../assets/images/bg-img.jpg";
import toast from "react-hot-toast";
import { IoMdPerson } from "react-icons/io";
import { MdEmail, MdPhone } from "react-icons/md";
import { Eye, EyeOff } from "lucide-react";

const UploadForm = ({}) => {
  const [formData, setFormData] = useState({
    resume: null,
    jdFile: null,
    jdText: "",
    similarityScore: null,
    error: null,
    uploadProgress: 0,
    setError: null,
  });
  const [jd, setJd] = useState();
  const [errors, setErrors] = useState(false);
  const { resume, jdFile, jdText, similarityScore, error } = formData;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileInputVisible, setFileInputVisible] = useState(false);
  const fileInputRef = useRef(null); // Ref for file input
  const [showUpload, setShowUpload] = useState(true);
  const [showSimilarityScore, setShowSimilarityScore] = useState(true);
  const [candidateID, setCandidateID] = useState(null);
  const [allJobs, setAllJobs] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState("null");
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  const navigate = useNavigate();

  const handleShowAppliedJobs = async (candidate) => {
    setCurrentCandidate(candidate);
    setShowDashboardModal(true);
    setIsLoadingJobs(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_KEY}/fetch_candidate_interview_status?candidate_id=${candidate.id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );

      const appliedJobs = response.data.candidate_applied_jobs;
      setAppliedJobs(appliedJobs);
      setIsLoadingJobs(false);
    } catch (err) {
      console.error("Error fetching applied jobs:", err);
      setIsLoadingJobs(false);
    }
  };

  const closeModal = () => {
    setShowDashboardModal(false);
    setAppliedJobs([]);
    setCurrentCandidate(null);
  };

  const redirectToDashboard = (jobId, candidate_id) => {
    // Implement your navigation logic here, e.g., using react-router
    navigate(`/candidate-dashboard?id=${jobId}&candidate_id=${candidate_id}`);
  };

  const handleBackButton = () => {
    setCurrentStep(1); // Go back to Step 1
    setSelectedCandidate(null); // Reset candidate
    setCandidateID(null); // Optional: reset ID
    setFormData((prev) => ({
      ...prev,
      resume: null,
      jdFile: null,
      jdText: "",
      similarityScore: null,
      error: null,
    }));
    setShowUpload(true);
    setShowSimilarityScore(false);
    setShowLink(false);
  };

  // ✅ Debug: log state on mount and change
  useEffect(() => {}, [selectedCandidate, selectedJob]);

  const toggleExpand = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleModalConfirm = () => {
    setFileInputVisible(true);
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click(); // Open file selection dialog
        setShowModal(false);
        setFileInputVisible(false);
      }
    }, 10);
  };
  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Reset error when a valid file is uploaded
      setFormData((prevState) => ({
        ...prevState,
        resume: file,
      }));
      e.target.value = null; // Reset file input value
      setErrors((prevState) => ({
        ...prevState,
        resume: null, // Clear the error for resume
      }));
    }
  };

  const handleJdTextChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      jdText: e.target.value,
    }));
    setErrors((prevState) => ({
      ...prevState,
      jdText: null,
      jdFile: null,
    }));
  };

  const handleUpload = async () => {
    if (!resume || (!jdFile && !jdText.trim() && !jd)) {
      setFormData((prevState) => ({
        ...prevState,

        error: "Please upload both Resume and Job description.",
      }));
      return;
    }

    const formData = new FormData();
    formData.append("resume", resume);
    jdFile
      ? formData.append("jd", jdFile)
      : formData.append("jd_text", jd ? jd : jdText);

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}/upload?jd_id=${selectedJob.id}&candidate_id=${selectedCandidate.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setFormData((prevState) => ({
              ...prevState,
              uploadProgress: percentCompleted,
            }));
          },
        }
      );

      if (response.data) {
        setFormData((prevState) => ({
          ...prevState,
          similarityScore: response.data,
          error: null,
        }));
        setShowUpload(false);
        setShowLink(true);
        setShowSimilarityScore(true);
        setCandidateID(response.data.candidate_id);
      } else {
        setFormData((prevState) => ({
          ...prevState,
          error: "No similarity score returned. Please try again.",
        }));
      }
    } catch (err) {
      setFormData((prevState) => ({
        ...prevState,
        error: err.response?.data?.error || "Something went wrong!",
      }));
    } finally {
      setLoading(false);
    }
  };

  // const fetchCandidatesAndJobs = async () => {
  //   try {
  //     const candidateRes = await axios.get(
  //       `${process.env.REACT_APP_API_KEY}/get_all_candidates`
  //     );
  //     const cleanedCandidates = candidateRes.data.all_candidates.map((c) => ({
  //       ...c,
  //       email: c.email.replace("mailto:", ""),
  //       name: `${c.first_name} ${c.last_name}`,
  //     }));
  //     setAllCandidates(cleanedCandidates);
  //   } catch (err) {
  //     console.error("Error fetching candidates:", err);
  //   }

  //   try {
  //     const jobRes = await axios.get(
  //       `${process.env.REACT_APP_API_KEY}/get_all_jobs`
  //     );
  //     setAllJobs(jobRes.data.all_jobs);
  //   } catch (err) {
  //     console.error("Error fetching jobs:", err);
  //   }
  // };
  const fetchCandidatesAndJobs = async () => {
    try {
      const timestamp = new Date().getTime(); // cache-busting
  
      const [candidateRes, jobRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_KEY}/get_all_candidates?ts=${timestamp}`, {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }),
        axios.get(`${process.env.REACT_APP_API_KEY}/get_all_jobs?ts=${timestamp}`, {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }),
      ]);
  
      const cleanedCandidates = candidateRes.data.all_candidates.map((c) => ({
        ...c,
        email: c.email.replace("mailto:", ""),
        name: `${c.first_name} ${c.last_name}`,
      }));
  
      setAllCandidates(cleanedCandidates);
      setAllJobs(jobRes.data.all_jobs);
    } catch (err) {
      console.error("Error fetching candidates or jobs:", err);
    }
  };
  
  useEffect(() => {
    fetchCandidatesAndJobs();
  }, []);

  const InterviewLink = async () => {
    const payload = {
      interview_url: `http://localhost:3000/ai-interview?id=${selectedJob?.id}&candidate_id=${selectedCandidate?.id}`,
    };
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}/provide_interview_link?jd_id=${selectedJob?.id}&candidate_id=${selectedCandidate?.id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );
      toast.success("Interview Link generated successfully");
    } finally {
      setLoading(false);
      setShowLink(false);
      setCurrentStep(1); // Go back to Step 1
      setSelectedCandidate(null); // Reset candidate
      setCandidateID(null); // Optional: reset ID
      setFormData((prev) => ({
        ...prev,
        resume: null,
        jdFile: null,
        jdText: "",
        similarityScore: null,
        error: null,
      }));
      setShowUpload(true); // Hide upload
      setShowSimilarityScore(false); // Hide score
      setShowLink(false);
    }
  };

  const DiscardCandidate = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}/discard_selected_candidate?jd_id=${selectedJob?.id}&candidate_id=${selectedCandidate?.id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );
      toast.success("Candidate discarded successfully");
    } finally {
      setLoading(false);
      setShowLink(false);
      setCurrentStep(1); // Go back to Step 1
      setSelectedCandidate(null); // Reset candidate
      setCandidateID(null); // Optional: reset ID
      setFormData((prev) => ({
        ...prev,
        resume: null,
        jdFile: null,
        jdText: "",
        similarityScore: null,
        error: null,
      }));
      setShowUpload(true); // Hide upload
      setShowSimilarityScore(false); // Hide score
      setShowLink(false);
    }
  };

  const [addCandidateForm, setAddCandidateForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
  });

  const [addCandidateErrors, setAddCandidateErrors] = useState({});
  const [addCandidateLoading, setAddCandidateLoading] = useState(false);

  const handleAddCandidateInputChange = (e) => {
    const { name, value } = e.target;
    setAddCandidateForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateAddCandidateForm = () => {
    let errors = {};

    if (!addCandidateForm.first_name?.trim())
      errors.first_name = "First name is required";
    if (!addCandidateForm.last_name?.trim())
      errors.last_name = "Last name is required";
    if (!addCandidateForm.email?.trim()) errors.email = "Email is required";
    if (!addCandidateForm.password?.trim())
      errors.password = "Password is required";
    if (!addCandidateForm.phone?.trim())
      errors.phone = "Phone number is required";

    if (
      addCandidateForm.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addCandidateForm.email)
    ) {
      errors.email = "Invalid email format";
    }

    if (addCandidateForm.password && addCandidateForm.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (addCandidateForm.phone && !/^\d{10}$/.test(addCandidateForm.phone)) {
      errors.phone = "Phone number must be 10 digits";
    }

    setAddCandidateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCandidateSubmit = async (e) => {
    e.preventDefault();
    if (!validateAddCandidateForm()) return;

    setAddCandidateLoading(true);

    const candidateData = {
      ...addCandidateForm,
      user_type: "Candidate",
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}/add_user`,
        candidateData,
        {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );

      toast.success("Candidate added successfully!");

      // Optionally: refresh list of candidates
      await fetchCandidatesAndJobs();

      // Reset and close modal
      setAddCandidateForm({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        phone: "",
      });
      setShowAddCandidateModal(false);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("This email is already registered.");
      } else {
        toast.error(error.response?.data?.error || "Failed to add candidate.");
      }
    } finally {
      setAddCandidateLoading(false);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <div
        className="w-full pt-10 p-6  min-h-[calc(100vh-96px)]"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* STEP 1: Candidate & Job Selection */}
        {currentStep === 1 && (
          <>
            <h2 className="text-2xl font-bold text-white mb-6">
              Step 1 : Select Candidate & Job
            </h2>
            .
            <div className="text-2xl font-bold text-white mb-6">
              <motion.button
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg shadow-md flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/hr-dashboard")}
              >
                HR Dashboard
              </motion.button>
            </div>
            <div className="flex gap-6">
              {/* Candidates Panel */}
              <div className="w-1/2 bg-white border border-gray-300 rounded-lg p-4 shadow-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">
                  Select Candidate
                </h3>
                <ul className="max-h-64 overflow-y-auto space-y-2">
                  {allCandidates.map((cand) => (
                    <li
                      key={cand.id}
                      className="flex items-center justify-between border-b py-2"
                    >
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="selectedCandidate"
                          value={cand.id}
                          onChange={() => setSelectedCandidate(cand)}
                          checked={selectedCandidate?.id === cand.id}
                        />
                        <span className="font-medium cursor-pointer text-gray-800">
                          {cand.name}
                        </span>
                      </label>
                      <div className="text-sm text-gray-600 text-right">
                        <div>{cand.email}</div>
                        <div>Candidate Id: {cand.id}</div>
                        <button
                          onClick={() => handleShowAppliedJobs(cand)}
                          className="text-blue-600 underline text-xs mt-1"
                        >
                          Applied Jobs
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowAddCandidateModal(true)}
                  className="mt-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-lg"
                >
                  Add Candidate
                </button>
              </div>

              {showAddCandidateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
                    <button
                      onClick={() => setShowAddCandidateModal(false)}
                      className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                    >
                      ✕
                    </button>
                    <h2 className="text-xl font-bold text-blue-800 mb-4">
                      Add New Candidate
                    </h2>
                    <form onSubmit={handleAddCandidateSubmit}>
                      <div className="grid gap-4">
                        <div className="relative">
                          <input
                            type="text"
                            name="first_name"
                            placeholder="First Name"
                            value={addCandidateForm.first_name}
                            onChange={handleAddCandidateInputChange}
                            className="border px-4 py-2 rounded w-full"
                          />

                          <IoMdPerson
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            size={20}
                          />
                          {addCandidateErrors.first_name && (
                            <p className="text-red-500 text-sm">
                              {addCandidateErrors.first_name}
                            </p>
                          )}
                        </div>

                        <div className="relative">
                          <input
                            type="text"
                            name="last_name"
                            placeholder="Last Name"
                            value={addCandidateForm.last_name}
                            onChange={handleAddCandidateInputChange}
                            className="border px-4 py-2 rounded w-full"
                          />
                          {addCandidateErrors.last_name && (
                            <p className="text-red-500 text-sm">
                              {addCandidateErrors.last_name}
                            </p>
                          )}
                          <IoMdPerson
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            size={20}
                          />
                        </div>

                        <div className="relative">
                          <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={addCandidateForm.email}
                            onChange={handleAddCandidateInputChange}
                            className="border px-4 py-2 rounded w-full"
                          />
                          {addCandidateErrors.email && (
                            <p className="text-red-500 text-sm">
                              {addCandidateErrors.email}
                            </p>
                          )}
                          <MdEmail
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            size={20}
                          />
                        </div>

                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Password"
                            value={addCandidateForm.password}
                            onChange={handleAddCandidateInputChange}
                            className="border px-4 py-2 rounded w-full"
                          />
                          {addCandidateErrors.password && (
                            <p className="text-red-500 text-sm">
                              {addCandidateErrors.password}
                            </p>
                          )}
                          <button
                            type="button"
                            className="absolute inset-y-0 right-3 flex items-center text-gray-600"
                            onClick={() => setShowPassword((prev) => !prev)}
                          >
                            {showPassword ? (
                              <EyeOff size={20} />
                            ) : (
                              <Eye size={20} />
                            )}
                          </button>
                        </div>

                        <div className="relative">
                          <input
                            type="tel"
                            name="phone"
                            placeholder="Phone Number"
                            value={addCandidateForm.phone}
                            onChange={handleAddCandidateInputChange}
                            className="border px-4 py-2 rounded w-full"
                          />
                          {addCandidateErrors.phone && (
                            <p className="text-red-500 text-sm">
                              {addCandidateErrors.phone}
                            </p>
                          )}
                          <MdPhone
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            size={20}
                          />
                        </div>

                        <button
                          type="submit"
                          disabled={addCandidateLoading}
                          className="bg-blue-600 text-white rounded py-2 hover:bg-blue-700"
                        >
                          {addCandidateLoading ? "Submitting..." : "Sign Up"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {showDashboardModal && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                  <div className="bg-white w-96 rounded-lg shadow-lg p-6 relative">
                    <button
                      onClick={closeModal}
                      className="absolute top-2 right-3 text-gray-500 hover:text-gray-700 text-xl"
                    >
                      &times;
                    </button>
                    <h2 className="text-lg font-semibold mb-4">
                      Applied Jobs for {currentCandidate?.name}
                    </h2>

                    {isLoadingJobs ? (
                      <div className="text-center py-10">
                        <svg
                          className="animate-spin h-6 w-6 text-gray-600 mx-auto"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                          ></path>
                        </svg>
                        <p className="mt-2 text-gray-500">
                          Loading applied jobs...
                        </p>
                      </div>
                    ) : appliedJobs.length > 0 ? (
                      <ul className="space-y-3">
                        {appliedJobs.map((job) => {
                          const status = job.interview_status.toLowerCase();
                          return (
                            <li key={job.job_id} className="border p-3 rounded">
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">
                                    {job.job_name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Job ID: {job.job_id}
                                  </div>
                                </div>

                                {status === "completed" ? (
                                  <button
                                    onClick={() =>
                                      redirectToDashboard(
                                        job.job_id,
                                        job.candidate_id
                                      )
                                    }
                                    className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                  >
                                    Go to Dashboard
                                  </button>
                                ) : (
                                  <button
                                    disabled
                                    className={`text-sm px-3 py-1 rounded cursor-not-allowed ${
                                      status === "ongoing"
                                        ? "bg-yellow-400 text-white"
                                        : "bg-red-600 text-white"
                                    }`}
                                  >
                                    {status}
                                  </button>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-gray-600">No jobs applied yet.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Jobs Panel */}
              <div className="w-1/2 bg-white border border-gray-300 rounded-lg p-4 shadow-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">
                  Select Job
                </h3>
                <ul className="max-h-64 overflow-y-auto space-y-2">
                  {allJobs.map((job) => (
                    <li
                      key={job.id}
                      className="flex flex-col border-b py-2 gap-1"
                    >
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 w-full">
                          <input
                            type="radio"
                            name="selectedJob"
                            value={job.id}
                            onChange={() => {
                              setSelectedJob(job);
                              setJd(job.job_description);
                            }}
                            checked={selectedJob?.id === job.id}
                          />
                          <span className="cursor-pointer text-gray-800 font-medium">
                            {job.job_title}
                          </span>
                        </label>
                        <div className="text-sm text-gray-600 text-right">
                          <div>Job Id: {job.id}</div>
                        </div>
                      </div>
                      {selectedJob?.id === job.id && (
                        <div className="mt-2 w-full bg-gray-100 text-gray-800 text-sm rounded-md p-3 shadow-inner">
                          <div
                            className={`${
                              expandedJobId === job.id ? "" : "line-clamp-3"
                            } overflow-hidden`}
                          >
                            {job.job_description}
                          </div>
                          <button
                            onClick={() => toggleExpand(job.id)}
                            className="mt-1 text-blue-600 text-xs hover:underline focus:outline-none"
                          >
                            {expandedJobId === job.id
                              ? "Show Less"
                              : "Show More"}
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setCurrentStep(2)}
                className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-lg"
                disabled={!selectedCandidate || !selectedJob}
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* STEP 2: Upload Resume and JD */}
        {currentStep === 2 && (
          <>
            <h2 className="text-2xl font-bold text-white my-4">
              Step 2 : Upload Resume & Job Description
            </h2>
            <div className="flex gap-6">
              {/* Resume Upload */}
              <div className="card p-6 border border-gray-300 bg-white rounded-lg shadow-lg block w-1/2">
                <div className="block">
                  <label className="block text-blue-800 mb-2 font-medium">
                    Resume*
                  </label>
                  <label className="w-full h-36 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:border-blue-500 transition">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleResumeChange}
                      accept=".pdf,.docx"
                    />
                    <i className="fa fa-cloud-upload text-5xl text-blue-500"></i>
                    <p className="text-gray-600 text-sm mt-2">
                      Click to upload or drag & drop
                    </p>
                    <p className="text-xs text-gray-500">PDF, DOCX</p>
                  </label>
                </div>

                {resume && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 border border-gray-300 rounded-lg bg-gray-100 flex justify-between items-center mt-3"
                  >
                    <div className="flex items-center gap-2">
                      <i className="fa fa-file text-blue-500 text-xl"></i>
                      <p className="text-gray-700 text-sm font-medium">
                        {resume.name}
                      </p>
                    </div>
                    <button
                      className="text-red-500 hover:text-red-700"
                      onClick={() => {
                        setShowSimilarityScore(false);
                        setShowUpload(true);
                        setFormData((prevState) => ({
                          ...prevState,
                          resume: null,
                        }));
                      }}
                    >
                      <i className="fa fa-times-circle text-lg"></i>
                    </button>
                  </motion.div>
                )}
                {errors?.resume && (
                  <p className="text-red-500 text-sm mt-1">{errors.resume}</p>
                )}
              </div>

              {/* JD Text Area */}
              <div className="card p-6 border border-gray-300 bg-white rounded-lg shadow-lg block w-1/2">
                <label className="block text-blue-800 mb-2 font-medium">
                  Job Description*
                </label>
                {jdFile && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 border border-gray-300  rounded-lg bg-gray-100 flex justify-between items-center mt-3"
                  >
                    <div className="flex items-center gap-2">
                      <i className="fa fa-file text-blue-500 text-xl"></i>
                      <p className="text-gray-700 text-sm font-medium">
                        {jdFile.name}
                      </p>
                    </div>
                    <button className="text-red-500 hover:text-red-700">
                      <i className="fa fa-times-circle text-lg"></i>
                    </button>
                  </motion.div>
                )}
                <textarea
                  className="mt-3 block w-full border border-gray-300 rounded-lg px-3 py-2 h-36 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  rows="4"
                  placeholder="Enter job description text here..."
                  value={jd ? jd : jdText}
                  onChange={handleJdTextChange}
                ></textarea>
                {(errors?.jdFile || errors?.jdText) && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.jdFile || errors.jdText}
                  </p>
                )}
              </div>
            </div>
            {!similarityScore && (
              <div className="flex justify-end mt-6">
                {/* Back Button */}
                <button
                  onClick={handleBackButton}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg"
                >
                  Back
                </button>
              </div>
            )}

            {/* Centering the Upload Button */}
            {showUpload && !showLink && (
              <div className="flex justify-center mt-6">
                <motion.button
                  className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-10 rounded-lg transition flex items-center gap-2"
                  onClick={handleUpload}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                  Upload
                </motion.button>
              </div>
            )}

            {similarityScore && showSimilarityScore && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="p-4 bg-white/90 backdrop-blur-lg shadow-2xl rounded-2xl border border-gray-300 mt-2"
              >
                <div className="text-center mb-1">
                  <h2 className="text-2xl font-bold text-blue-800">
                    Similarity Score
                  </h2>
                  <p className="text-2xl font-extrabold text-green-600 mt-1">
                    {String(similarityScore?.similarity_score || 0).replace(
                      /\.$/,
                      ""
                    )}
                    %
                  </p>
                </div>
              </motion.div>
            )}
          </>
        )}

        {error && (
          <p className="mt-4 text-center text-lg text-red-600 font-medium">
            {error}
          </p>
        )}

        {showLink && (
          <div className=" text-xl font-medium justify-center flex mt-4 gap-3">
            <button
              className="bg-red-600 text-white py-2 px-6 rounded-lg"
              onClick={(e) => {
                e.preventDefault();
                DiscardCandidate();
              }}
            >
              Reject
            </button>
            <button
              className="bg-green-600 text-white py-2 px-6 rounded-lg"
              onClick={(e) => {
                e.preventDefault();
                InterviewLink();
              }}
            >
              Accept
            </button>
          </div>
        )}

        <FileUploadConformationModal
          isOpen={showModal}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
        />
      </div>
    </>
  );
};

export default UploadForm;
